import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  WritableSignal,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { UpsellComponent } from '../upsell/upsell.component';
import { Subject, debounceTime, first, fromEvent, takeUntil } from 'rxjs';
import { IconNameType } from '../icons/icon-name-type.enum';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { OrderUpsellService } from '../../core/services/order-upsell.service';
import { OrderUpsellWrapper } from '../../core/models/ordering/order-upsell-wrapper';
import { Upsell } from '../../core/models/menu/upsell.model';
import { Utils } from '../../core/utils/utils';
import { IconComponent } from '../icons/icon.component';

@Component({
  selector: 'cwo-upsells',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    UpsellComponent,
    RoundIconComponent,
    IconComponent,
  ],
  templateUrl: './upsells.component.html',
  styleUrl: './upsells.component.scss',
})
export class UpsellsComponent implements OnInit, OnDestroy
{
  orderUpsellService = inject(OrderUpsellService);

  iconNameType = IconNameType;
  isScrolling = false;
  leftButtonVisible = false;
  rightButtonVisible = false;
  upsellItems:WritableSignal<Upsell[]> = signal<Upsell[]>([]);
  headerPadding = input<string>();
  upsellsMargin = input<string>();
  sisterUpsells = input<any>([]);
  isInItemEditor = input<boolean>(false);
  triggeringArea = input.required<string>();
  expanded = false;

  private destroy$ = new Subject<void>();

  readonly upsellRef = viewChild<ElementRef>('upsell');

  private cdRef = inject(ChangeDetectorRef);

  constructor() {}

  ngOnInit(): void {
    if(!this.isInItemEditor()) {
      this.fetchUpsells();
    } else {
      setTimeout(() => {
        this.setButtonsState();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchUpsells(): void {
    this.orderUpsellService
      .fetchOrderUpsells()
      .pipe(takeUntil(this.destroy$))
      .subscribe((orderUpsells: OrderUpsellWrapper) => {
        this.upsellItems.set(Utils.flatten((orderUpsells?.orderBasedUpsells || []).flatMap(group => group.upsellItems)));
        setTimeout(() => {
          this.setButtonsState();
        }, 0)
      });
  }

  async arrowButtonClicked(direction: 'left' | 'right'): Promise<void> {
    if (this.isScrolling) {
      return;
    }

    this.isScrolling = true;

    try {
      const upsellRef = this.upsellRef();
      await this.scrollToElement(upsellRef?.nativeElement, {
        left: this.calculateStep(direction, upsellRef?.nativeElement.scrollWidth),
        behavior: 'smooth',
      });
      this.setButtonsState();
    } finally {
      this.isScrolling = false;
    }
  }

  private scrollToElement(
    element: HTMLElement | undefined,
    options: any
  ): Promise<void> {
    if (!element) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const scrollSubscription = fromEvent(element, 'scroll')
        .pipe(
          debounceTime(100),
          first(),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          resolve();
          scrollSubscription.unsubscribe();
        });
      element.scrollBy(options);
    });
  }

  setButtonsState() {
    const upsellRef = this.upsellRef();
    if (!upsellRef?.nativeElement) return;

    const element = upsellRef.nativeElement;
    const currentWidth = element.offsetWidth + element.scrollLeft;
    
    this.leftButtonVisible = element.scrollLeft > 1;
    this.rightButtonVisible = currentWidth < element.scrollWidth - 1;
    this.cdRef.detectChanges();
  }

  handleUpsellAdded(value: boolean) {
    if (value) {
      this.fetchUpsells();
    }
  }


  calculateStep(direction: string, max: number) {
    const upsellRef = this.upsellRef();
    const currentWidth = upsellRef?.nativeElement.offsetWidth + upsellRef?.nativeElement.scrollLeft;
    const leftScroll = currentWidth - upsellRef?.nativeElement.offsetWidth;
    
    const step = 180;
    return direction === 'right'
      ? Math.min(step, max - currentWidth)
      : Math.max(-step, -leftScroll);
  }

  scrollToBottom() {
    const itemEditorWrapper = document.getElementById(
      'item-editor-scrollable-wrapper'
    );
    if (itemEditorWrapper) {
      itemEditorWrapper.scrollTo({
        top: itemEditorWrapper.scrollHeight,
        behavior: 'smooth'
      })
    }
  }
}
